import { motion } from 'framer-motion';
import { useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import FloatingButtons from '../home/components/FloatingButtons';
import { useInView } from '../home/hooks/useInView';

const Colomiers = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.1 });
  const [servicesRef, servicesInView] = useInView({ threshold: 0.1 });
  const [testimonialsRef, testimonialsInView] = useInView({ threshold: 0.1 });

  useEffect(() => {
    document.title = 'Réparation de fenêtres à Colomiers – Intervention rapide 24h/24 | Vitrerie 31';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert en réparation de fenêtres à Colomiers (31770). Intervention rapide sous 2h pour vos fenêtres PVC, bois, alu. Devis gratuit. ☎ 05 54 54 25 45');
    }
  }, []);

  const services = [
    { icon: 'ri-tools-line', title: 'Réparation Fenêtres PVC', desc: 'Joints, mécanismes, vitrage' },
    { icon: 'ri-hammer-line', title: 'Fenêtres Bois', desc: 'Rénovation et entretien' },
    { icon: 'ri-window-line', title: 'Fenêtres Aluminium', desc: 'Réparation et remplacement' },
    { icon: 'ri-shield-check-line', title: 'Vitrerie d\'urgence', desc: 'Intervention 24h/24' }
  ];

  const testimonials = [
    {
      name: 'Marie Dubois',
      location: 'Quartier En Jacca',
      text: 'Fenêtre cassée un dimanche matin, intervention en moins de 2h ! Service impeccable et prix honnête.',
      rating: 5
    },
    {
      name: 'Jean-Pierre Martin',
      location: 'Centre-ville Colomiers',
      text: 'Réparation de mes fenêtres PVC vieillissantes. Travail soigné, équipe professionnelle. Je recommande vivement.',
      rating: 5
    },
    {
      name: 'Sophie Leclerc',
      location: 'Quartier Perget',
      text: 'Problème de condensation résolu rapidement. Excellent conseil et suivi. Merci pour votre réactivité !',
      rating: 5
    }
  ];

  const problemesTypiques = [
    { icon: 'ri-water-flash-line', title: 'Condensation excessive', desc: 'Fréquent dans les quartiers résidentiels' },
    { icon: 'ri-wind-line', title: 'Infiltrations d\'air', desc: 'Joints usés par le climat toulousain' },
    { icon: 'ri-lock-unlock-line', title: 'Mécanismes bloqués', desc: 'Usure due aux variations thermiques' },
    { icon: 'ri-contrast-drop-line', title: 'Vitrage endommagé', desc: 'Bris de glace et fissures' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section ref={heroRef} className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-white to-orange-50"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-map-pin-line"></i>
              <span>Colomiers - Haute-Garonne (31770)</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Réparation de fenêtres à <strong className="text-teal-600">Colomiers</strong>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Intervention rapide <strong>sous 2h</strong> pour tous vos problèmes de fenêtres PVC, bois et aluminium
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <a
                href="tel:0554542545"
                className="inline-flex items-center gap-2 bg-teal-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-teal-700 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
              >
                <i className="ri-phone-line text-xl"></i>
                05 54 54 25 45
              </a>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-50 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer border-2 border-teal-600 active:scale-95"
              >
                <i className="ri-message-line text-xl"></i>
                Devis gratuit
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-time-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Intervention rapide</h3>
                <p className="text-gray-600 text-sm">Sous 2h à Colomiers</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-map-pin-2-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">À 12 km de Toulouse</h3>
                <p className="text-gray-600 text-sm">Tous quartiers desservis</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-24-hours-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Urgences 24h/24</h3>
                <p className="text-gray-600 text-sm">7 jours sur 7</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section ref={servicesRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos services à <strong className="text-teal-600">Colomiers</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experts en réparation de fenêtres pour tous les quartiers de Colomiers
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-teal-50 to-orange-50 rounded-lg p-6 hover:shadow-xl transition-all cursor-pointer group"
              >
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className={`${service.icon} text-3xl text-teal-600 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Problèmes Typiques */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Problèmes fréquents à <strong className="text-teal-600">Colomiers</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Nous connaissons les spécificités climatiques et architecturales de votre ville
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {problemesTypiques.map((probleme, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-all"
              >
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <i className={`${probleme.icon} text-2xl text-orange-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{probleme.title}</h3>
                <p className="text-gray-600 text-sm">{probleme.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gradient-to-r from-teal-500 to-orange-500 rounded-lg p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-4">Pourquoi ces problèmes à Colomiers ?</h3>
            <p className="text-lg mb-6 opacity-90 max-w-3xl mx-auto">
              Située à <strong>12 km à l'ouest de Toulouse</strong>, Colomiers connaît un climat océanique dégradé avec des variations thermiques importantes. Les quartiers résidentiels comme <strong>En Jacca, Perget et le Centre-ville</strong> sont particulièrement touchés par la condensation et l'usure des joints. Nos équipes interviennent rapidement dans tous les secteurs de la commune.
            </p>
          </div>
        </div>
      </section>

      {/* Témoignages Clients */}
      <section ref={testimonialsRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Avis de nos clients à <strong className="text-teal-600">Colomiers</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Ils nous ont fait confiance pour leurs fenêtres
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-teal-50 to-orange-50 rounded-lg p-6 shadow-lg"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-500 text-xl"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                <div className="border-t border-gray-200 pt-4">
                  <p className="font-bold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600 flex items-center gap-1">
                    <i className="ri-map-pin-line"></i>
                    {testimonial.location}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* NOUVEAU BLOC 1 - Dépannage Urgence */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="relative"
        >
          <div className="relative w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="https://public.readdy.ai/ai/img_res/4a4429569ebc032ba658a5f1b835a548.jpg"
              alt="Dépannage urgence fenêtres Colomiers"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-teal-900/60 via-transparent to-transparent"></div>
            
            <div className="absolute bottom-6 left-6 bg-white rounded-xl shadow-xl p-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                  <i className="ri-alarm-warning-line text-red-600 text-2xl"></i>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900">Urgence 24h/24</p>
                  <p className="text-sm text-gray-600">Intervention rapide</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h3 className="text-3xl font-bold text-gray-900 mb-6">
            <strong className="text-teal-600">Dépannage d'Urgence 24h/24</strong> à Colomiers
          </h3>
          <p className="text-gray-600 mb-4 leading-relaxed">
            Une <strong>vitre cassée</strong>, une <strong>fenêtre bloquée</strong> ou un <strong>volet roulant coincé</strong> à Colomiers ? Notre service de <strong>dépannage d'urgence fenêtre à Colomiers</strong> est disponible 24h/24 et 7j/7 pour sécuriser votre domicile rapidement. Nous intervenons dans l'heure qui suit votre appel pour tous types d'urgences vitrerie.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Nos techniciens d'urgence réalisent la <strong>sécurisation de fenêtre cassée</strong>, le <strong>remplacement de vitre en urgence</strong>, le <strong>déblocage de volet roulant</strong> et la <strong>réparation de serrure de fenêtre</strong>. Intervention garantie sous 1h à Colomiers, Tournefeuille, Blagnac et toute la zone ouest de Toulouse.
          </p>

          <div className="bg-red-50 border-l-4 border-red-600 rounded-lg p-6 mb-6">
            <h4 className="font-bold text-gray-900 mb-3 flex items-center">
              <i className="ri-time-line text-red-600 mr-2 text-xl"></i>
              Intervention Express
            </h4>
            <div className="space-y-2">
              <div className="flex items-center text-gray-700">
                <i className="ri-check-line text-red-600 mr-3"></i>
                <span className="text-sm"><strong>Moins d'1h</strong> : Arrivée sur place à Colomiers</span>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-check-line text-red-600 mr-3"></i>
                <span className="text-sm"><strong>Sécurisation immédiate</strong> : Pose de protection temporaire</span>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-check-line text-red-600 mr-3"></i>
                <span className="text-sm"><strong>Réparation définitive</strong> : Dans les 24h suivantes</span>
              </div>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex items-start">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-shield-check-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Sécurisation Immédiate</h4>
                <p className="text-gray-600 text-sm">Pose de protection temporaire pour sécuriser votre domicile en attendant la réparation</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-phone-line text-2xl text-orange-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Disponible 24h/24 - 7j/7</h4>
                <p className="text-gray-600 text-sm">Équipe d'urgence joignable jour et nuit, week-ends et jours fériés</p>
              </div>
            </div>
          </div>

          <a
            href="tel:0554542545"
            className="inline-block bg-gradient-to-r from-red-500 to-red-600 text-white px-8 py-4 rounded-full font-semibold hover:from-red-600 hover:to-red-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line mr-2"></i>
            Appel Urgence : 05 54 54 25 45
          </a>
        </motion.div>
      </div>

      {/* NOUVEAU BLOC 2 - Rénovation Énergétique */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="order-2 lg:order-1"
        >
          <h3 className="text-3xl font-bold text-gray-900 mb-6">
            <strong className="text-orange-600">Rénovation Énergétique</strong> de vos Fenêtres à Colomiers
          </h3>
          <p className="text-gray-600 mb-4 leading-relaxed">
            Avec plus de 40 000 habitants, Colomiers compte de nombreux logements anciens nécessitant une <strong>rénovation énergétique des fenêtres</strong>. Nous vous accompagnons dans votre projet de <strong>remplacement de fenêtres à Colomiers</strong> pour améliorer votre confort thermique et réduire vos factures d'énergie jusqu'à 30%.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Nos solutions incluent l'<strong>installation de fenêtres double vitrage haute performance</strong>, le <strong>remplacement de fenêtres simple vitrage</strong>, la <strong>pose de fenêtres PVC à rupture de pont thermique</strong> et l'<strong>installation de volets isolants</strong>. Nous vous conseillons sur les aides financières disponibles (MaPrimeRénov', éco-PTZ) pour votre projet à Colomiers.
          </p>

          <div className="bg-gradient-to-r from-orange-50 to-teal-50 rounded-xl p-6 mb-6">
            <h4 className="font-bold text-gray-900 mb-4 flex items-center">
              <i className="ri-money-euro-circle-line text-orange-600 mr-2 text-xl"></i>
              Aides Financières 2024
            </h4>
            <div className="space-y-3">
              <div className="bg-white rounded-lg p-3">
                <p className="font-semibold text-gray-900 mb-1">MaPrimeRénov'</p>
                <p className="text-sm text-gray-600">Jusqu'à 100€ par fenêtre selon vos revenus</p>
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="font-semibold text-gray-900 mb-1">Éco-PTZ</p>
                <p className="text-sm text-gray-600">Prêt à taux zéro jusqu'à 50 000€</p>
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="font-semibold text-gray-900 mb-1">TVA réduite 5,5%</p>
                <p className="text-sm text-gray-600">Sur matériel et main d'œuvre</p>
              </div>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex items-center text-gray-700">
              <i className="ri-check-line text-orange-600 mr-3 text-xl"></i>
              <span>Certification RGE pour bénéficier des aides</span>
            </div>
            <div className="flex items-center text-gray-700">
              <i className="ri-check-line text-orange-600 mr-3 text-xl"></i>
              <span>Accompagnement dans vos démarches administratives</span>
            </div>
            <div className="flex items-center text-gray-700">
              <i className="ri-check-line text-orange-600 mr-3 text-xl"></i>
              <span>Devis détaillé conforme aux exigences des aides</span>
            </div>
          </div>

          <a
            href="#contact"
            className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-full font-semibold hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line mr-2"></i>
            Devis Rénovation + Aides
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="relative order-1 lg:order-2"
        >
          <div className="relative w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="https://readdy.ai/api/search-image?query=modern%20energy%20efficient%20home%20renovation%20showing%20before%20and%20after%20window%20replacement%20high%20performance%20double%20glazing%20installation%20thermal%20insulation%20improvement%20residential%20building%20in%20colomiers%20toulouse%20area%20demonstrating%20energy%20savings%20eco%20friendly%20upgrade%20simple%20clean%20background&width=800&height=600&seq=colomiers-energy-renovation-v1&orientation=landscape"
              alt="Rénovation énergétique fenêtres Colomiers"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-orange-900/60 via-transparent to-transparent"></div>
            
            <div className="absolute bottom-6 right-6 bg-white rounded-xl shadow-xl p-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                  <i className="ri-leaf-line text-green-600 text-2xl"></i>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900">Éco-Rénovation</p>
                  <p className="text-sm text-gray-600">Certifié RGE</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Quartiers desservis */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Tous les quartiers de <strong className="text-teal-600">Colomiers</strong>
            </h2>
            <p className="text-lg text-gray-600">Intervention rapide dans toute la commune</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {['Centre-ville', 'En Jacca', 'Perget', 'Ramassiers', 'Val d\'Aran', 'Garoussal', 'Plaine', 'Virebent'].map((quartier, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-all text-center"
              >
                <i className="ri-map-pin-2-fill text-2xl text-teal-600 mb-2"></i>
                <p className="font-semibold text-gray-900">{quartier}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-500 to-orange-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Besoin d'une réparation de fenêtre à Colomiers ?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Intervention rapide sous 2h - Devis gratuit - Urgences 24h/24
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:0554542545"
              className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-50 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
            >
              <i className="ri-phone-line text-xl"></i>
              Appelez maintenant
            </a>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
            >
              <i className="ri-message-line text-xl"></i>
              Demander un devis
            </a>
          </div>
        </div>
      </section>

      {/* Liens vers autres villes */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Nous intervenons aussi dans les villes voisines
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Toulouse</a>
            <span className="text-gray-400">•</span>
            <a href="/blagnac" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Blagnac</a>
            <span className="text-gray-400">•</span>
            <a href="/tournefeuille" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Tournefeuille</a>
            <span className="text-gray-400">•</span>
            <a href="/muret" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Muret</a>
            <span className="text-gray-400">•</span>
            <a href="/balma" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Balma</a>
            <span className="text-gray-400">•</span>
            <a href="/ramonville" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Ramonville</a>
            <span className="text-gray-400">•</span>
            <a href="/cugnaux" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Cugnaux</a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default Colomiers;
